import { useMutation } from "@tanstack/react-query";
import type { ZodRawShape } from "zod";

import type { FormDataType } from "@rootreeweb/rui-forms";
import {
	getFieldLabel,
	parseSchema,
	type FormState,
	type ParsedSchema,
	type TypedFormState,
} from "@rootreeweb/zod-to-elements";

import { formLookup } from "../forms/index.js";
import type {
	FormDefinition,
	FormMutator,
	FormSubmissionOptions,
	UseFormOptions,
} from "../types.js";
import { makeAPIRequest } from "./makeAPIRequest.js";

/**
 * Replaces the field names in one form with equivalent field names in another
 * form. This is useful for automations that read by field name. Both forms must
 * have the same fields in the same order.
 *
 * @param form_data The form data to map.
 * @param remoteFormID The form ID to map the fields to.
 * @returns The form data with the fields mapped.
 */
export function mapFieldsToForm(
	form_data: FormState,
	remoteFormID: FormDefinition<ZodRawShape> | string,
): FormState {
	const remoteForm =
		typeof remoteFormID === "string" ? formLookup[remoteFormID] : remoteFormID;
	if (!remoteForm) {
		return form_data;
	}
	const remoteFields = parseSchema(remoteForm.schema.shape).fields;
	const formDataArray = Object.values(form_data);

	const newFormData: FormState = {};
	for (let i = 0; i < remoteFields.length; i++) {
		const field = remoteFields[i];
		const fieldLabel = getFieldLabel(field);
		if (fieldLabel) {
			if (field.definition.typeName === "ZodBoolean") {
				newFormData[field.field] = formDataArray[i] ? formDataArray[i] : "No";
			} else {
				newFormData[fieldLabel] = formDataArray[i];
			}
		}
	}
	return newFormData;
}

/**
 * Provide a mutator for the form submission.
 *
 * @example
 * 	const mutator = useFormSubmission(formDefinition, useFormOptions);
 * 	const onSubmit = ()=>mutator.mutate(payload);
 * 	if (mutator.status === "loading") {
 * 	return <div>Loading...</div>;
 * 	}
 *
 * @template T The type of the form data.
 * @param formDefinition The form definition.
 * @param useFormOptions The options for the form.
 * @returns The form mutator.
 */
export function useFormSubmission<T extends ZodRawShape>(
	formDefinition: FormDefinition<T>,
	useFormOptions: UseFormOptions,
): FormMutator<FormDataType<T>> {
	type S = FormDataType<T>;
	const { formID, schema, submissionOptions: rawOptions } = formDefinition;
	const fields = parseSchema(schema.shape);
	const mutator: FormMutator<S> = useMutation({
		mutationFn: async (data: TypedFormState<S>) => {
			const options = parseOptions(rawOptions, data);
			let form_data = labelValues(data, fields);
			if (formDefinition.formOptions?.mapFieldsToForm) {
				form_data = mapFieldsToForm(
					form_data,
					formDefinition.formOptions.mapFieldsToForm,
				);
			}
			const body = JSON.stringify({
				form_data: JSON.stringify(form_data),
				form_id: formID,
				options: JSON.stringify(options || {}),
				signature: formDefinition.signature,
			});
			return await makeAPIRequest<TypedFormState<S>>(useFormOptions, body);
		},
		mutationKey: [formID],
	});
	return mutator;
}

/**
 * Labels the values with the field labels.
 *
 * @param values The values to label.
 * @param schema The schema to use for the labels.
 * @returns The values with the labels.
 */
function labelValues(values: FormState, schema: ParsedSchema): FormState {
	const fields = schema.fields;
	const newValues: FormState = { ...values };
	for (const key in values) {
		const activeField = fields.find((f) => f.field === key);
		if (!activeField) {
			continue;
		}
		const fieldLabel = getFieldLabel(activeField);
		if (!fieldLabel) {
			continue;
		}
		// This is required to maintain the field order for mapping.
		// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
		delete newValues[key];
		if (activeField.definition.typeName === "ZodBoolean") {
			newValues[key] = values[key] ? fieldLabel : "No";
		} else {
			newValues[fieldLabel] = values[key];
		}
	}
	return newValues;
}

/**
 * Parses the options for the form. If the option is a function, it will be
 * called with the data.
 *
 * @template T The type of the form data.
 * @param rawOptions The raw options to parse.
 * @param data The data to pass to the functions.
 * @returns The parsed options.
 */
function parseOptions<T extends object>(
	rawOptions: FormSubmissionOptions<T> | undefined,
	data: TypedFormState<T>,
): FormSubmissionOptions<T> {
	const options: Record<
		string,
		FormSubmissionOptions<T>[keyof FormSubmissionOptions<T>]
	> = {};
	if (rawOptions) {
		for (const key in rawOptions) {
			const optionKey = key as keyof FormSubmissionOptions<T>;
			const rawValue = rawOptions[optionKey];
			if (typeof rawValue === "function") {
				options[optionKey] = rawValue(data);
			} else {
				options[optionKey] = rawOptions[optionKey];
			}
		}
	}
	return options;
}
